import {
  Button,
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  TooltipProvider,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { PaginationControls } from '@/shared/ui';
import { SortableHead } from '@/shared/ui/data-table';
import { TABLE_COL_WIDTH } from '@/shared/constants';
import { EmptyState } from '@/shared/ui';

import { ADMIN_TABLE_STYLES } from '../adminTableStyles';
import { ToolsTableV2Row } from './ToolsTableV2Row';
import { ToolsTableV2SkeletonRow } from './ToolsTableV2SkeletonRow';
import type { ToolsTableV2Props } from './ToolsTableV2.types';
import { useToolsTableV2 } from './useToolsTableV2';

/** Matches the MUI table's loading row count, so neither implementation loads taller. */
const SKELETON_ROWS = Array.from({ length: 10 }, (_, index) => index);

const NAME_COLUMN = 'displayName';

export function ToolsTableV2({
  tools,
  isLoading,
  isError,
  onRetry,
  searchQuery = '',
  typeFilter,
  onManageWorkspaces,
  onEditAgent,
  onDeleteAgent,
  externalToolsAgentId,
  managedToolIds,
  sortId,
  onSortChange,
  showLastModified,
}: ToolsTableV2Props) {
  const { t } = useTranslation('admin');
  const table = useToolsTableV2({
    tools,
    typeFilter,
    searchQuery,
    sortId,
    onSortChange,
    showLastModified,
  });

  if (isError) {
    return (
      <EmptyState role="alert" title={t('admin.tools.couldnTLoadTheTools')}>
        <Button onClick={onRetry}>{t('admin.tools.retry')}</Button>
      </EmptyState>
    );
  }

  // A refetch keeps the rows on screen; only a cold load has nothing to show.
  const isColdLoad = isLoading && tools.length === 0;

  if (!isColdLoad && table.totalCount === 0) {
    // A list emptied by a search or a type filter is the reader's own doing; an empty list
    // under neither is a genuinely empty organization.
    return table.isNarrowed ? (
      <EmptyState
        title={t('admin.tools.noToolsMatchThisFilter')}
        description={t('admin.tools.tryADifferentTypeFilter')}
      />
    ) : (
      <EmptyState title={t('admin.tools.noToolsYet')} />
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      {/* The pager scrolls with the rows and follows the last one: pinned under a scrolling list,
          a page of rows read as both paged and endless. `mt-auto` still sets it on the card's
          bottom edge when a page is shorter than the card. */}
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-auto">
        <div className="px-section pb-section">
          <Datatable
            aria-label={t('admin.tools.toolsList')}
            aria-busy={isColdLoad || undefined}
            className={ADMIN_TABLE_STYLES}
          >
            <DatatableHeader>
              <DatatableRow>
                <SortableHead
                  column={NAME_COLUMN}
                  label={t('admin.toolsTable.name')}
                  sortBy={table.sortBy}
                  sortDirection={table.sortDirection}
                  onSort={table.toggleNameSort}
                  // Not the shared Name token: that one balances the Projects-route
                  // tables against a fixed data region, and this table has a
                  // conditional Type column, so its region is not fixed. It also
                  // sits in the settings rail, with no table to line up with.
                  // Browsers ignore `max-width` on a table cell, so the width is what
                  // holds the column at 400px; the max only guards a fixed layout.
                  className="w-100 max-w-100"
                />
                {table.showType && (
                  <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
                    {t('admin.toolsTable.type')}
                  </DatatableHead>
                )}
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.person}>
                  {t('admin.toolsTable.visibility')}
                </DatatableHead>
                {showLastModified && (
                  <DatatableHead scope="col" className={TABLE_COL_WIDTH.dateText}>
                    {t('admin.toolsTable.lastModified')}
                  </DatatableHead>
                )}
                {/* The kebab column carries no heading: the icon in each row says what it is. */}
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions} />
              </DatatableRow>
            </DatatableHeader>

            <DatatableBody>
              {isColdLoad
                ? SKELETON_ROWS.map((index) => (
                    <ToolsTableV2SkeletonRow
                      key={index}
                      showType={table.showType}
                      showLastModified={showLastModified}
                    />
                  ))
                : table.rows.map((tool) => (
                    <ToolsTableV2Row
                      key={tool.id}
                      tool={tool}
                      showType={table.showType}
                      showLastModified={showLastModified}
                      isManaged={managedToolIds?.includes(tool.id) ?? false}
                      isExternalToolsAgent={tool.id === externalToolsAgentId}
                      onManageWorkspaces={onManageWorkspaces}
                      onEditAgent={onEditAgent}
                      onDeleteAgent={onDeleteAgent}
                    />
                  ))}
            </DatatableBody>
          </Datatable>
        </div>

        <PaginationControls
          page={table.page + 1}
          totalItems={table.totalCount}
          pageSize={table.rowsPerPage}
          onPageChange={(nextPage) => {
            table.goToPage(nextPage - 1);
          }}
          className="mt-auto p-section"
        />
      </div>
    </TooltipProvider>
  );
}

ToolsTableV2.displayName = 'ToolsTableV2';
